import {useState} from 'react';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {BotAvatar} from './BotComponents';
import ChatInput from './ChatInput';
import {dataClient} from '../data/dataClient';
import {sendChat,promoteToMemory,ingestKnowledgeSource} from '../services/api';
import {estimateAgenticOperations,getUsageSummary} from '../services/aoMeter';
import {emitShareFrame} from '../services/shareBus';
import {workspacePath} from '../services/paths';

const {FiUsers,FiFilePlus,FiBookmark,FiArrowRight,FiXCircle,FiPaperclip}=FiIcons;

// frontend.md §10: show the estimated cost when the user is NEAR the limit
// (this reply uses N AO). "Near" = the next lead-consult send would cross 80%.
const NEAR_LIMIT_RATIO=0.8;

export default function ChatWindow({module,lead,messages=[],planLabel,onPlan}) {
  const [consulting,setConsulting]=useState(null);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState(null);
  const [activeBotId,setActiveBotId]=useState(lead?.id || null);

  const state=dataClient.peek();
  const usage=getUsageSummary(state);

  // Live PB workspaces hydrate asynchronously and a module's team can lack a
  // Lead (or not exist yet) — never dereference an undefined lead (fixes the
  // ChatWindow.jsx:23 production crash). Placed after all hooks.
  if (!lead) {
    return (
      <section className="chat-window panel">
        <div className="chat-head">
          <div className="inline">
            <span className="pulse-ring">?</span>
            <div>
              <b>No team Lead yet</b>
              <small>Chat starts once a Lead is available</small>
            </div>
          </div>
        </div>
        <div className="messages">
          <div className="message-row bot">
            <div className="message-bubble">
              <p>This module needs a team with exactly one Lead bot before chat can start. You can manage teams in the Bots screen.</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Resolve team roster: Lead + any teammates on this module's team
  const team=(state.botTeams || []).find(
    (t)=> t.module===module || t.leadBot===lead.id || t.id===lead.teamId
  );
  const memberIds=team?.memberBots || [];
  const rosterBots=(state.bots || []).filter(
    (b)=> b.enabled && (b.id===lead.id || memberIds.includes(b.id) || (lead.consultableBots || []).includes(b.id))
  );
  const botOptions=rosterBots.some((b)=> b.id===lead.id) ? rosterBots : [lead,...rosterBots];
  const activeBot=botOptions.find((b)=> b.id===(activeBotId || lead.id)) || lead;

  const consultedBots=(lead?.consultableBots || [])
    .map((id)=> state.bots.find((bot)=> bot.id===id && bot.enabled))
    .filter(Boolean);

  // Direct specialist talk does not consult other bots; Lead consults teammates
  const nextSendCost=estimateAgenticOperations({
    lead: activeBot,
    consultedBots: activeBot.isLead ? consultedBots : []
  });
  const nearLimit=usage.allowance > 0
    && (usage.used + nextSendCost) / usage.allowance >= NEAR_LIMIT_RATIO;

  const pinnedMessageIds=new Set(
    (state.businessMemory || [])
      .map((memory)=> memory.sourceMessageId)
  );

  async function pinMessage(message) {
    try {
      await promoteToMemory(message.id);
    } catch (pinError) {
      setError({type: 'generic',message: pinError.message});
    }
  }

  async function submit(prompt,files=[],targetBotId) {
    if (!prompt.trim() && !files.length) return;
    if (busy) return;

    const targetBot=botOptions.find((b)=> b.id===(targetBotId || activeBot.id)) || activeBot;
    setBusy(true);
    setError(null);

    try {
      // Ingest attachments to real Dexie files + knowledgeSources pipeline
      const attachedMeta=[];
      for (const file of files) {
        const sizeStr=`${Math.max(1, Math.round(file.size / 1024))} KB`;
        const botScope=targetBot.knowledge_scope || targetBot.knowledgeScope || '';
        const rec=await dataClient.create('files',{
          name: file.name,
          size: sizeStr,
          tags: ['chat-attachment',module,targetBot.name.toLowerCase()],
          visibility: [targetBot.id],
          knowledgeScope: botScope
        });
        await ingestKnowledgeSource({
          business: state.business?.id,
          bot: targetBot.id,
          sourceType: 'file',
          sourceRef: rec.id,
          name: file.name,
          knowledgeScope: botScope
        });
        attachedMeta.push({id: rec.id,name: file.name,size: sizeStr});
      }

      await dataClient.addMessage(module,{
        id: `user_${Date.now()}`,
        sender: 'user',
        text: prompt,
        botId: targetBot.id,
        attachments: attachedMeta.length ? attachedMeta : undefined
      });

      await sendChat({
        module,
        botId: targetBot.id,
        text: prompt,
        onConsulting: setConsulting
      });

      // T12 live share: mirror the exchange to Viewer-Guests while a share is
      // active. Frames are read-only HOST state; nothing here grants viewers
      // data access.
      const shareState=dataClient.peek();
      const thread=(shareState.messages?.[module] || []).slice(-8).map((m)=> ({
        role: m.sender==='user' ? 'user' : 'bot',
        content: m.text || '',
        botName: m.botId ? (shareState.bots || []).find((b)=> b.id===m.botId)?.name : undefined
      }));
      emitShareFrame({ screenSlug: module, payload: { kind: 'chat', messages: thread } });
    } catch (sendError) {
      // frontend.md §5.4/§10: AO_PLAN_LIMIT_REACHED is a plan-limit UI state
      // with an upgrade CTA — never a generic failure. Guardrail blocks show
      // the policy message verbatim. NO_PROVIDER_CONNECTED (fix #2): this bot
      // has no BYO key — tell the user exactly where to connect one. Anything
      // else is a plain inline error.
      if (sendError.code==='AO_PLAN_LIMIT_REACHED') {
        setError({type: 'plan-limit',message: sendError.message});
      } else if (sendError.code==='CONTENT_POLICY_BLOCKED') {
        setError({type: 'policy',message: sendError.message});
      } else if (sendError.code==='NO_PROVIDER_CONNECTED') {
        setError({type: 'provider',message: sendError.message});
      } else {
        setError({type: 'generic',message: sendError.message});
      }
    } finally {
      setConsulting(null);
      setBusy(false);
    }
  }

  return (
    <section className="chat-window panel">
      <div className="chat-head">
        <div className="inline">
          <BotAvatar bot={activeBot} />
          <div>
            <b>{activeBot.name}</b>
            <small>{activeBot.role}{activeBot.id!==lead.id ? ` (direct · ${lead.name}'s team)` : ''}</small>
          </div>
        </div>
        <span className="online">Online</span>
      </div>
      <div className="messages">
        {messages.map((message)=> (
          <div key={message.id} className={`message-row ${message.sender}`}>
            <div className="message-bubble">
              <p>{message.text}</p>
              {message.attachments?.length > 0 && (
                <div className="message-attachments">
                  {message.attachments.map((att)=> (
                    <span key={att.id || att.name} className="attachment-chip">
                      <SafeIcon icon={FiPaperclip} />
                      {att.name}
                    </span>
                  ))}
                </div>
              )}
              {message.consultedBots?.length > 0 && (
                <div className="consulted">
                  <SafeIcon icon={FiUsers} />
                  Consulted {message.consultedBots.map((bot)=> bot.name).join(',')}
                </div>
              )}
              <button
                type="button"
                className="pin-action"
                onClick={()=> pinMessage(message)}
                disabled={pinnedMessageIds.has(message.id) || busy}
                title={pinnedMessageIds.has(message.id)
                  ? 'Pinned to business memory'
                  : 'Pin this message to business memory'}
              >
                <SafeIcon icon={FiBookmark} />
                {pinnedMessageIds.has(message.id) ? 'Pinned' : 'Pin'}
              </button>
            </div>
          </div>
        ))}
        {consulting && (
          <div className="consulting-state">
            <span className="pulse-ring" style={{background: consulting.color}}>
              {consulting.name[0]}
            </span>
            <div>
              <b>Consulting {consulting.name}…</b>
              <small>{consulting.role} is reviewing the question</small>
            </div>
          </div>
        )}
      </div>
      {nearLimit && !error && (
        <div className="ao-estimate">
          This reply uses <b>{nextSendCost} AO</b> — {usage.remaining} of{' '}
          {usage.allowance} left on {usage.planName}.
        </div>
      )}
      {error?.type==='plan-limit' && (
        <div className="plan-limit" role="alert">
          <div>
            <b>Plan limit reached</b>
            <p>{error.message}</p>
          </div>
          <Link className="button secondary compact" to={workspacePath('settings',state)}>
            Upgrade plan
            <SafeIcon icon={FiArrowRight} />
          </Link>
        </div>
      )}
      {error?.type==='provider' && (
        <div className="provider-gate" role="alert">
          <div>
            <b>Connect a provider to chat</b>
            <p>{error.message}</p>
          </div>
          <Link className="button primary compact" to={workspacePath('models',state)}>
            Connect your API key
            <SafeIcon icon={FiArrowRight} />
          </Link>
        </div>
      )}
      {error && error.type!=='plan-limit' && error.type!=='provider' && (
        <div className="chat-error" role="alert">
          <SafeIcon icon={FiXCircle} />
          <span>{error.message}</span>
        </div>
      )}
      {messages.some((message)=> message.sender==='bot' && message.consultedBots?.length) && (
        <button className="save-plan" onClick={onPlan}>
          <SafeIcon icon={FiFilePlus} />
          {planLabel}
        </button>
      )}
      <ChatInput
        lead={lead}
        botOptions={botOptions}
        botId={activeBot.id}
        onBotChange={setActiveBotId}
        onSubmit={submit}
        busy={busy}
        placeholder={`Ask ${activeBot.name} anything…`}
      />
    </section>
  );
}
